import { SLICE_TYPE } from "@niivue/niivue"
import type { NvSceneController } from "@niivue/nvreact"
import { useCallback, useEffect, useRef, useState } from "react"

import { sliceTypeOptions } from "./viewer-slice-layout-picker"

/** Maps a slice layout key to its SLICE_TYPE if it's a single-plane view. */
function layoutKeyToSliceType(layoutKey: string | null): SLICE_TYPE | null {
  if (!layoutKey) return null
  const option = sliceTypeOptions[layoutKey]
  if (!option) return null
  const st = option.sliceType as SLICE_TYPE
  if (st === SLICE_TYPE.AXIAL || st === SLICE_TYPE.CORONAL || st === SLICE_TYPE.SAGITTAL) return st
  return null
}

/** Returns the crosshairPos component index for a given slice type. */
function sliceAxis(st: SLICE_TYPE): 0 | 1 | 2 {
  if (st === SLICE_TYPE.AXIAL) return 2
  if (st === SLICE_TYPE.CORONAL) return 1
  return 0
}

/** Returns the volume dimension count for the through-plane axis. dims is [ndim, dimX, dimY, dimZ]. */
function throughPlaneDimCount(dims: number[], st: SLICE_TYPE): number {
  if (st === SLICE_TYPE.AXIAL) return dims[3] ?? 1
  if (st === SLICE_TYPE.CORONAL) return dims[2] ?? 1
  return dims[1] ?? 1
}

interface ViewerSliceSliderProps {
  viewerIndex: number
  scene: NvSceneController
  /** The slice layout key from UI state (e.g. "slice:axial"), or null for default layout. */
  layoutKey: string | null
}

export function ViewerSliceSlider({ viewerIndex, scene, layoutKey }: ViewerSliceSliderProps) {
  const [sliceIndex, setSliceIndex] = useState(0)
  const [totalSlices, setTotalSlices] = useState(1)
  const [sliceType, setSliceType] = useState<SLICE_TYPE | null>(null)
  const suppressCallbackRef = useRef(false)

  const readState = useCallback(() => {
    const nv = scene.getNiivue(viewerIndex)
    if (!nv?.volumes[0]?.dims) {
      setSliceType(null)
      return
    }

    // Determine current slice type
    let resolvedSliceType = layoutKeyToSliceType(layoutKey)
    if (resolvedSliceType === null) {
      // Default layout — check screenSlices
      const screenSlice = nv.screenSlices?.[0]
      if (screenSlice && nv.screenSlices.length === 1) {
        const aCS = screenSlice.axCorSag as SLICE_TYPE
        if (aCS === SLICE_TYPE.AXIAL || aCS === SLICE_TYPE.CORONAL || aCS === SLICE_TYPE.SAGITTAL) {
          resolvedSliceType = aCS
        }
      }
    }

    if (resolvedSliceType === null) {
      setSliceType(null)
      return
    }

    const dims = nv.volumes[0].dims
    const count = throughPlaneDimCount(dims, resolvedSliceType)
    const axis = sliceAxis(resolvedSliceType)
    const frac = nv.scene.crosshairPos[axis]
    const idx = Math.round(frac * (count - 1))

    setSliceType(resolvedSliceType)
    setTotalSlices(count)
    setSliceIndex(idx)
  }, [scene, viewerIndex, layoutKey])

  // Read initial state and wire up onLocationChange
  useEffect(() => {
    readState()

    const nv = scene.getNiivue(viewerIndex)
    if (!nv) return

    const previousCallback = nv.onLocationChange
    const wrappedCallback = (data: unknown) => {
      previousCallback?.(data)
      if (!suppressCallbackRef.current) {
        readState()
      }
    }
    nv.onLocationChange = wrappedCallback

    return () => {
      // Restore previous callback only if ours is still installed
      if (nv.onLocationChange === wrappedCallback) {
        nv.onLocationChange = previousCallback
      }
    }
  }, [readState, scene, viewerIndex])

  // Re-read when layout changes
  useEffect(() => {
    readState()
  }, [layoutKey, readState])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (sliceType === null) return
      const nv = scene.getNiivue(viewerIndex)
      if (!nv?.volumes[0]?.dims) return

      // The input value is inverted (max - value) so top = last slice
      const rawValue = Number.parseInt(event.target.value, 10)
      const targetSlice = totalSlices - 1 - rawValue
      const dims = nv.volumes[0].dims
      const count = throughPlaneDimCount(dims, sliceType)
      const axis = sliceAxis(sliceType)
      const currentFrac = nv.scene.crosshairPos[axis]
      const currentSlice = Math.round(currentFrac * (count - 1))
      const delta = targetSlice - currentSlice

      if (delta === 0) return

      const dx = axis === 0 ? delta : 0
      const dy = axis === 1 ? delta : 0
      const dz = axis === 2 ? delta : 0

      suppressCallbackRef.current = true
      nv.moveCrosshairInVox(dx, dy, dz)
      suppressCallbackRef.current = false

      setSliceIndex(targetSlice)
    },
    [scene, sliceType, totalSlices, viewerIndex]
  )

  if (sliceType === null || totalSlices <= 1) return null

  // Invert so top of slider = last slice (superior), bottom = first slice (inferior)
  const invertedValue = totalSlices - 1 - sliceIndex

  return (
    <>
      <input
        type="range"
        min={0}
        max={totalSlices - 1}
        value={invertedValue}
        onChange={handleChange}
        className="nv-slice-slider pointer-events-auto"
        aria-label="Scroll slices"
        title={`Slice ${sliceIndex + 1} / ${totalSlices}`}
      />
      <span className="pointer-events-none shrink-0 whitespace-nowrap text-[10px] leading-none text-white/70 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
        {sliceIndex + 1}/{totalSlices}
      </span>
    </>
  )
}
